/**
 * What a YouTube upload must carry, by YouTube's API Services policies
 * (Required Minimum Functionality) and the Data API's videos resource:
 *  - the uploader chooses the video's privacy (public, unlisted, private),
 *    never a default of ours;
 *  - the uploader says whether the video is made for kids (COPPA): an
 *    explicit yes or no, never assumed;
 *  - the uploader says whether the video has realistic altered or synthetic
 *    content (status.containsSyntheticMedia): an explicit yes or no, never
 *    assumed and never read off the media. The composer answers yes for a
 *    video the app's AI made, where the uploader sees it and can change it;
 *    what is stored is what is sent;
 *  - the title and description are sent exactly as the uploader wrote them:
 *    no truncation, no added #Shorts or tags.
 *
 * The choices are stored per post in `platformOptions.YOUTUBE`, as text like
 * every other option ('true' / 'false' for yes/no), and read back here by the
 * API (schedule checks), the worker (what it sends) and the web (the
 * composer's YouTube panel).
 */

export const YOUTUBE_PRIVACY_STATUSES = ['public', 'unlisted', 'private'] as const;

export type YouTubePrivacyStatus = (typeof YOUTUBE_PRIVACY_STATUSES)[number];

/** The keys of `platformOptions.YOUTUBE`, besides its caption (the description). */
export const YOUTUBE_OPTION = {
  title: 'title',
  privacyStatus: 'privacyStatus',
  madeForKids: 'madeForKids',
  containsSyntheticMedia: 'containsSyntheticMedia',
  /** Comma-separated, as the uploader typed them. */
  tags: 'tags',
  categoryId: 'categoryId',
} as const;

/**
 * The standard categories a video can be filed under (snippet.categoryId):
 * YouTube's assignable categories, the same ids in every region. The
 * composer names them through i18n (`youtube.category.names.<id>`). No
 * category leaves the choice to YouTube.
 */
export const YOUTUBE_CATEGORY_IDS = [
  '1', // Film & Animation
  '2', // Autos & Vehicles
  '10', // Music
  '15', // Pets & Animals
  '17', // Sports
  '19', // Travel & Events
  '20', // Gaming
  '22', // People & Blogs
  '23', // Comedy
  '24', // Entertainment
  '25', // News & Politics
  '26', // Howto & Style
  '27', // Education
  '28', // Science & Technology
  '29', // Nonprofits & Activism
] as const;

export type YouTubeCategoryId = (typeof YOUTUBE_CATEGORY_IDS)[number];

export function isYouTubeCategoryId(value: unknown): value is YouTubeCategoryId {
  return typeof value === 'string' && (YOUTUBE_CATEGORY_IDS as readonly string[]).includes(value);
}

/** The yes/no keys among the above: stored as 'true' or 'false'. */
export const YOUTUBE_BOOLEAN_KEYS: readonly string[] = [YOUTUBE_OPTION.madeForKids, YOUTUBE_OPTION.containsSyntheticMedia];

/** snippet.title: at most 100 characters. */
export const YOUTUBE_TITLE_MAX_CHARS = 100;
/** snippet.description: at most 5,000 bytes of UTF-8, not characters. */
export const YOUTUBE_DESCRIPTION_MAX_BYTES = 5000;
/** snippet.tags: 500 characters for the whole list, commas and quotes counted. */
export const YOUTUBE_TAGS_MAX_CHARS = 500;

/** YouTube's help pages the composer panel links to. */
export const YOUTUBE_MADE_FOR_KIDS_HELP_URL = 'https://support.google.com/youtube/answer/9528076';
export const YOUTUBE_SYNTHETIC_MEDIA_HELP_URL = 'https://support.google.com/youtube/answer/14328491';

/** The uploader's choices for one YouTube video, read from its options. */
export interface YouTubeVideoChoices {
  title: string;
  privacyStatus: string | null;
  /** null until the uploader answers. */
  madeForKids: boolean | null;
  /** null until the uploader answers; never taken from the media. */
  containsSyntheticMedia: boolean | null;
  tags: string[];
  categoryId: string | null;
}

/** A problem with a YouTube video, with a stable code and a sentence a person can act on. */
export interface YouTubeProblem {
  code:
    | 'YOUTUBE_TITLE_REQUIRED'
    | 'YOUTUBE_TITLE_TOO_LONG'
    | 'YOUTUBE_ANGLE_BRACKETS'
    | 'YOUTUBE_DESCRIPTION_TOO_LONG'
    | 'YOUTUBE_PRIVACY_REQUIRED'
    | 'YOUTUBE_PRIVACY_INVALID'
    | 'YOUTUBE_MADE_FOR_KIDS_REQUIRED'
    | 'YOUTUBE_SYNTHETIC_MEDIA_REQUIRED'
    | 'YOUTUBE_TAGS_TOO_LONG'
    | 'YOUTUBE_CATEGORY_INVALID';
  message: string;
}

/** The video resource parts sent to videos.insert / videos.update. */
export interface YouTubeVideoResource {
  snippet: {
    title: string;
    description: string;
    tags?: string[];
    categoryId?: string;
  };
  status: {
    privacyStatus: YouTubePrivacyStatus;
    selfDeclaredMadeForKids: boolean;
    containsSyntheticMedia: boolean;
  };
}

export function isYouTubePrivacyStatus(value: unknown): value is YouTubePrivacyStatus {
  return typeof value === 'string' && (YOUTUBE_PRIVACY_STATUSES as readonly string[]).includes(value);
}

const encoder = new TextEncoder();

/** A text's length in UTF-8 bytes, which is how YouTube measures a description. */
export function utf8ByteLength(text: string): number {
  return encoder.encode(text).length;
}

const graphemes = new Intl.Segmenter('en', { granularity: 'grapheme' });

/** A text's length in graphemes, so an emoji or an Indic syllable counts once. */
export function youtubeTitleLength(title: string): number {
  let n = 0;
  for (const _segment of graphemes.segment(title)) n++;
  return n;
}

/** YouTube refuses '<' and '>' in a title or description. */
export function hasAngleBrackets(text: string): boolean {
  return /[<>]/.test(text);
}

/** The tags typed as "a, b, c", trimmed, empty ones dropped, in order. */
export function parseYouTubeTags(text: string | null | undefined): string[] {
  if (!text) return [];
  return text.split(',').map((t) => t.trim()).filter((t) => t !== '');
}

/** The tag list's length as YouTube counts it: commas between, quotes round a tag with a space. */
export function youtubeTagsLength(tags: readonly string[]): number {
  if (tags.length === 0) return 0;
  const each = tags.reduce((sum, tag) => sum + tag.length + (/\s/.test(tag) ? 2 : 0), 0);
  return each + tags.length - 1;
}

/** A stored yes/no: 'true' / 'false' (or a real boolean); anything else is unanswered. */
function yesNo(value: unknown): boolean | null {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return null;
}

/** What a video without the altered-or-synthetic answer is refused with (API, worker). */
export const YOUTUBE_SYNTHETIC_MEDIA_REQUIRED_MESSAGE =
  'Say whether your YouTube video has altered or synthetic content (containsSyntheticMedia: true or false).';

/** The choices in `platformOptions.YOUTUBE` (or a job's extras). */
export function youtubeChoices(options: Readonly<Record<string, unknown>> | null | undefined): YouTubeVideoChoices {
  const o = options ?? {};
  const text = (key: string): string => {
    const v = o[key];
    return typeof v === 'string' ? v.trim() : '';
  };
  const privacy = text(YOUTUBE_OPTION.privacyStatus);
  const category = text(YOUTUBE_OPTION.categoryId);
  return {
    title: text(YOUTUBE_OPTION.title),
    privacyStatus: privacy || null,
    madeForKids: yesNo(o[YOUTUBE_OPTION.madeForKids]),
    containsSyntheticMedia: yesNo(o[YOUTUBE_OPTION.containsSyntheticMedia]),
    tags: parseYouTubeTags(text(YOUTUBE_OPTION.tags)),
    categoryId: category || null,
  };
}

/**
 * What stops a video being uploaded with these choices and this description,
 * before YouTube is asked anything. Empty when it can be uploaded.
 */
export function youtubeProblems(choices: YouTubeVideoChoices, description: string): YouTubeProblem[] {
  const problems: YouTubeProblem[] = [];
  if (!choices.title) {
    problems.push({ code: 'YOUTUBE_TITLE_REQUIRED', message: 'YouTube needs a title: write one in the YouTube options.' });
  } else if (youtubeTitleLength(choices.title) > YOUTUBE_TITLE_MAX_CHARS) {
    problems.push({
      code: 'YOUTUBE_TITLE_TOO_LONG',
      message: `The YouTube title is longer than ${YOUTUBE_TITLE_MAX_CHARS} characters.`,
    });
  }
  if (hasAngleBrackets(choices.title) || hasAngleBrackets(description)) {
    problems.push({
      code: 'YOUTUBE_ANGLE_BRACKETS',
      message: 'YouTube does not accept < or > in a title or description: remove them.',
    });
  }
  if (utf8ByteLength(description) > YOUTUBE_DESCRIPTION_MAX_BYTES) {
    problems.push({
      code: 'YOUTUBE_DESCRIPTION_TOO_LONG',
      message: `The YouTube description is longer than YouTube's ${YOUTUBE_DESCRIPTION_MAX_BYTES}-byte limit: shorten it.`,
    });
  }
  if (!choices.privacyStatus) {
    problems.push({ code: 'YOUTUBE_PRIVACY_REQUIRED', message: 'Choose who can watch your YouTube video: public, unlisted or private.' });
  } else if (!isYouTubePrivacyStatus(choices.privacyStatus)) {
    problems.push({ code: 'YOUTUBE_PRIVACY_INVALID', message: 'Choose public, unlisted or private for your YouTube video.' });
  }
  if (choices.madeForKids === null) {
    problems.push({ code: 'YOUTUBE_MADE_FOR_KIDS_REQUIRED', message: 'Say whether your YouTube video is made for kids.' });
  }
  if (choices.containsSyntheticMedia === null) {
    problems.push({ code: 'YOUTUBE_SYNTHETIC_MEDIA_REQUIRED', message: YOUTUBE_SYNTHETIC_MEDIA_REQUIRED_MESSAGE });
  }
  if (youtubeTagsLength(choices.tags) > YOUTUBE_TAGS_MAX_CHARS) {
    problems.push({ code: 'YOUTUBE_TAGS_TOO_LONG', message: `YouTube tags may total ${YOUTUBE_TAGS_MAX_CHARS} characters at most.` });
  }
  if (choices.categoryId !== null && !isYouTubeCategoryId(choices.categoryId)) {
    problems.push({ code: 'YOUTUBE_CATEGORY_INVALID', message: "Choose one of YouTube's categories, or none to let YouTube decide." });
  }
  return problems;
}

/**
 * The snippet and status YouTube receives for these choices: exactly what the
 * uploader chose and wrote, nothing defaulted, nothing appended, nothing
 * read off the media. Throws when the choices are incomplete (privacy,
 * made for kids or the altered-or-synthetic answer missing), which the
 * schedule check prevents.
 */
export function youtubeVideoResource(choices: YouTubeVideoChoices, description: string): YouTubeVideoResource {
  const problems = youtubeProblems(choices, description);
  const first = problems[0];
  if (first) throw new Error(first.message);
  if (
    !isYouTubePrivacyStatus(choices.privacyStatus) ||
    choices.madeForKids === null ||
    choices.containsSyntheticMedia === null
  ) {
    throw new Error('YouTube privacy, made-for-kids and altered-content answers must be given.');
  }
  return {
    snippet: {
      title: choices.title,
      description,
      ...(choices.tags.length > 0 ? { tags: choices.tags } : {}),
      ...(choices.categoryId ? { categoryId: choices.categoryId } : {}),
    },
    status: {
      privacyStatus: choices.privacyStatus,
      selfDeclaredMadeForKids: choices.madeForKids,
      containsSyntheticMedia: choices.containsSyntheticMedia,
    },
  };
}
